import React, { memo } from "react";
import { Box, Flex, Icon, Text, chakra, HStack } from "@chakra-ui/react";
import { X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "components/ds";
import { FilterCondition, FilterOperator } from "./FilterBar";
import { FILTERS_REGISTRY, operatorLabels } from "../filters/filterRegistry";
import { format } from "date-fns";

// Helper function to get a human-readable operator label
const getOperatorLabel = (operator: FilterOperator): string => {
  return operatorLabels[operator] || operator;
};

// Helper function to format the display value based on type and operator
const getDisplayValue = (value: any, operator: FilterOperator): string => {
  // For isEmpty/isNotEmpty, no value is needed
  if (operator === "isEmpty" || operator === "isNotEmpty") {
    return "";
  }

  // Handle different value types
  if (value === null || value === undefined) {
    return "null";
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return "[]";
    return `[${value.filter(Boolean).join(", ")}]`;
  }

  if (typeof value === "string") {
    // Check if it's a date string (in ISO format)
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      try {
        return format(new Date(value), "MMM d, yyyy");
      } catch (e) {
        return value;
      }
    }
    return value;
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  return String(value);
};

// Styled animated tag for our filter chips
const AnimatedTag = chakra(motion.div, {
  baseStyle: {
    display: "flex",
    alignItems: "center",
    py: 1,
    px: 2.5,
    borderRadius: "5px",
    fontSize: "xs",
    bg: "white",
    color: "gray.700",
    maxWidth: "280px",
    overflow: "hidden",
    boxShadow: "xs",
    borderWidth: "1px",
    borderColor: "gray.200",
    flexShrink: 0,
  },
});

interface FilterChipsProps {
  activeFilters: FilterCondition[];
  onRemoveFilter: (filter: FilterCondition) => void;
  onClearAll: () => void;
}

const FilterChips: React.FC<FilterChipsProps> = ({
  activeFilters,
  onRemoveFilter,
  onClearAll,
}) => {
  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <Flex
      w="100%"
      gap={2}
      my={3}
      p={2}
      bg="gray.50"
      borderRadius="md"
      alignItems="center"
      borderWidth="1px"
      borderColor="gray.100"
      overflowX="auto"
      overflowY="hidden"
      css={{
        "&::-webkit-scrollbar": {
          height: "6px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(0,0,0,0.1)",
          borderRadius: "3px",
        },
      }}
    >
      <AnimatePresence>
        {activeFilters.map((filter) => (
          <AnimatedTag
            key={filter.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.15 } as any}
            cursor={"pointer"}
            onClick={() => onRemoveFilter(filter)}
          >
            <Icon
              as={FILTERS_REGISTRY[filter.type]?.icon}
              boxSize={2.5}
              color="gray.500"
              mr={1}
            />
            <Text as="span" fontWeight="500" noOfLines={1}>
              {FILTERS_REGISTRY[filter.type]?.label || filter.type}
            </Text>
            <Text
              as="span"
              mx={1}
              fontWeight="400"
              fontSize="10px"
              color="gray.600"
              flexShrink={0}
            >
              {getOperatorLabel(filter.operator)}
            </Text>
            <Text
              as="span"
              fontWeight="500"
              noOfLines={1}
              maxW="150px"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {getDisplayValue(filter.value, filter.operator)}
            </Text>
            <Box
              as="button"
              display="flex"
              alignItems="center"
              justifyContent="center"
              ml={1}
              borderRadius="sm"
              transition="all 0.2s"
              _hover={{ bg: "blackAlpha.50" }}
              onClick={(e) => {
                e.stopPropagation();
                onRemoveFilter(filter);
              }}
            >
              <Icon as={X} boxSize={2} color="gray.500" />
            </Box>
          </AnimatedTag>
        ))}

        <Button
          size="xs"
          variant="subtle"
          tone="neutral"
          onClick={onClearAll}
          style={{ marginLeft: "auto" }}
        >
          Clear all
        </Button>
      </AnimatePresence>
    </Flex>
  );
};

export default memo(FilterChips);
